import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { formatFecha, COLORES, colorDe, idsPersona } from './tipos'
import PersonaPicker, { usePersonas } from '../PersonaPicker'

// One Planilla cell per column type (models.AtributoTipo). Every editor
// follows the same rules: click opens it, a change is sent at most once, and
// a failed save leaves the cell outlined in red with the old value.
// `soloLectura`: the value only, nothing to click (no Mantenimiento editing).

const base = 'w-full min-w-[8rem] text-left px-2 py-1 rounded text-sm truncate transition-colors'
const inputCls = 'w-full min-w-[8rem] border border-gray-300 rounded px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200'

// Shared save bookkeeping: saving -> dimmed, failed -> red outline.
function useSave(onSave) {
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)
  const save = async (v) => {
    setSaving(true); setFailed(false)
    try { await onSave(v) } catch { setFailed(true) }
    setSaving(false)
  }
  const cls = `${saving ? 'opacity-50' : ''} ${failed ? 'ring-1 ring-red-400 bg-red-50' : ''}`
  return { save, cls }
}

function Display({ value, onClick, cls, children }) {
  if (!onClick) {
    return (
      <span title={typeof value === 'string' ? value : undefined}
        className={`${base} block ${value ? 'text-gray-800' : 'text-gray-300'} ${cls}`}>
        {children ?? (value || '—')}
      </span>
    )
  }
  return (
    <button type="button" onClick={onClick} title={typeof value === 'string' ? value : undefined}
      className={`${base} hover:bg-gray-100 ${value ? 'text-gray-800' : 'text-gray-300'} ${cls}`}>
      {children ?? (value || '—')}
    </button>
  )
}

// Free text and numbers: an input; Enter or leaving saves, Esc cancels.
function TypedCell({ value, onSave, numeric, soloLectura }) {
  const { t } = useTranslation('engineering')
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [invalid, setInvalid] = useState(false)
  const done = useRef(false)   // Enter saves, then blur fires too
  const { save, cls } = useSave(onSave)

  const start = () => { setDraft(value ?? ''); setInvalid(false); done.current = false; setEditing(true) }
  // false = not a number: Enter keeps the editor open to fix it.
  const commit = () => {
    if (done.current) return true
    const next = draft.trim()
    if (numeric && next && !/^-?\d+([.,]\d+)?$/.test(next)) { setInvalid(true); return false }
    done.current = true
    setEditing(false)
    if (next !== (value ?? '')) save(next || null)
    return true
  }
  const cancel = () => { done.current = true; setEditing(false) }

  if (!editing) {
    return <Display value={value} onClick={soloLectura ? null : start} cls={`${cls} ${numeric ? 'tabular-nums' : ''}`} />
  }
  return (
    <input autoFocus value={draft} inputMode={numeric ? 'decimal' : undefined}
      title={invalid ? t('unidades.planilla.notANumber') : undefined}
      onChange={e => { setDraft(e.target.value); setInvalid(false) }}
      onBlur={() => { if (!commit()) cancel() }}   // leaving with a non-number drops it
      onKeyDown={e => {
        if (e.key === 'Enter') { e.preventDefault(); commit() }
        else if (e.key === 'Escape') cancel()
      }}
      className={`${inputCls} ${numeric ? 'tabular-nums' : ''} ${invalid ? 'ring-1 ring-red-400' : ''}`} />
  )
}

// Date: opens the browser's calendar straight away; picking a day saves.
function FechaCell({ value, onSave, soloLectura }) {
  const [editing, setEditing] = useState(false)
  const ref = useRef(null)
  const { save, cls } = useSave(onSave)

  useEffect(() => {
    if (!editing || !ref.current) return
    ref.current.focus()
    try { ref.current.showPicker?.() } catch { /* not all browsers allow it */ }
  }, [editing])

  if (!editing) {
    return <Display value={value} onClick={soloLectura ? null : () => setEditing(true)} cls={`${cls} tabular-nums`}>
      {value ? formatFecha(value) : '—'}
    </Display>
  }
  return (
    <input ref={ref} type="date" defaultValue={value || ''}
      onChange={e => { const v = e.target.value || null; if (v !== (value || null)) save(v) }}
      onBlur={() => setEditing(false)}
      onKeyDown={e => { if (e.key === 'Escape' || e.key === 'Enter') setEditing(false) }}
      className={inputCls} />
  )
}

const NUEVA = '__nueva__'

// Estado: a closed list. "+ Nueva opción…" adds a state to the column
// itself (onAddOpcion), then picks it.
function EstadoCell({ value, onSave, columna, onAddOpcion, soloLectura }) {
  const opciones = columna.opciones || []
  const { t } = useTranslation('engineering')
  const [editing, setEditing] = useState(false)
  const { save, cls } = useSave(onSave)
  const fuera = value && !opciones.includes(value)   // an option removed after it was used

  const onChange = async (e) => {
    const v = e.target.value
    setEditing(false)
    if (v === NUEVA) {
      const nombre = (prompt(t('unidades.planilla.newOptionPrompt')) || '').trim()
      if (!nombre) return
      const ok = await onAddOpcion(nombre)
      if (ok) save(ok)
      return
    }
    const next = v || null
    if (next !== (value || null)) save(next)
  }

  if (!editing) {
    return (
      <Display value={value} onClick={soloLectura ? null : () => setEditing(true)} cls={cls}>
        {value
          ? <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
              fuera ? 'bg-white text-gray-500 border border-dashed border-gray-300'
                    : COLORES[colorDe(columna, value)].pill}`}
              title={fuera ? t('unidades.planilla.notInOptions') : value}>{value}</span>
          : '—'}
      </Display>
    )
  }
  return (
    <select autoFocus defaultValue={value || ''} onChange={onChange} onBlur={() => setEditing(false)}
      onKeyDown={e => { if (e.key === 'Escape') setEditing(false) }}
      className={inputCls}>
      <option value="">—</option>
      {fuera && <option value={value}>{value}</option>}
      {opciones.map(o => <option key={o} value={o}>{o}</option>)}
      <option value={NUEVA}>{t('unidades.planilla.newOption')}</option>
    </select>
  )
}

// Persona: one or more people of the register, picked by typing; shown by
// name. Stored as a JSON list of their ids.
function PersonaCell({ value, onSave, soloLectura }) {
  const { porId } = usePersonas()
  const [editing, setEditing] = useState(false)
  const { save, cls } = useSave(onSave)
  const ids = idsPersona(value)
  const nombres = ids.map(id => porId[id]?.name || `#${id}`)

  if (!editing) {
    return (
      <Display value={nombres.join(', ')} onClick={soloLectura ? null : () => setEditing(true)} cls={cls}>
        {ids.length ? nombres.join(', ') : '—'}
      </Display>
    )
  }
  return (
    <PersonaPicker multiple autoFocus value={ids} className="min-w-[14rem]"
      onChange={nuevos => {
        const next = nuevos.length ? JSON.stringify([...new Set(nuevos)].sort((a, b) => a - b)) : null
        if (next !== (value || null)) save(next)
      }}
      onClose={() => setEditing(false)} />
  )
}

// Archivo: shows how many files; the Planilla opens its files dialog.
// Read-only, an empty cell has nothing to open (no files, no attaching).
function ArchivoCell({ archivos, onOpen, soloLectura }) {
  const { t } = useTranslation('engineering')
  const n = archivos?.length || 0
  if (soloLectura && !n) return <span className={`${base} block text-gray-300`}>—</span>
  return (
    <button type="button" onClick={onOpen}
      className={`${base} hover:bg-gray-100 ${n ? 'text-gray-800' : 'text-gray-400'}`}
      title={n ? archivos.map(a => a.filename).join('\n') : t('unidades.planilla.attach')}>
      {n ? `📎 ${n}` : t('unidades.planilla.attach')}
    </button>
  )
}

export default function PlanillaCell({ columna, value, archivos, onSave, onAddOpcion, onOpenArchivos, soloLectura = false }) {
  switch (columna.tipo) {
    case 'numero':  return <TypedCell value={value} onSave={onSave} numeric soloLectura={soloLectura} />
    case 'fecha':   return <FechaCell value={value} onSave={onSave} soloLectura={soloLectura} />
    case 'estado':  return <EstadoCell value={value} onSave={onSave} columna={columna} onAddOpcion={onAddOpcion} soloLectura={soloLectura} />
    case 'archivo': return <ArchivoCell archivos={archivos} onOpen={onOpenArchivos} soloLectura={soloLectura} />
    case 'persona': return <PersonaCell value={value} onSave={onSave} soloLectura={soloLectura} />
    default:        return <TypedCell value={value} onSave={onSave} soloLectura={soloLectura} />
  }
}
